import React, { useState } from 'react';

import { ArrowIcon } from '../SvgIcons/Arrow';

import HighlightTitle, { HighlightTitleProps } from './HighlightTitle';
import FeaturedImage, { FeaturedImageProps } from './FeaturedImage';

import {
  AccordionWrapper,
  ButtonWrapper,
  DifferentiationWrapper,
  DekWrapper,
  ImageWrapper,
  MenuItemDescription,
  MenuItemTitle,
  MenuItem,
  MenuWrapper,
  SectionWrapper,
  Sidebar,
  StyledAccordion,
  UnselectedItemWrapper,
  TitleWrapper,
  HiddenContent,
  AccordionImageWrapper,
  HiddenOnDesktop,
  OnlyOnDesktop,
} from './Differentiation.styles';
import { ButtonV3 } from '../ButtonV3';

type DifferentiatorContentBaseProps = {
  /** Used to set the state for currently selected section */
  id: string;
  subtitle: string;
  description: React.ReactNode;
  button?: {
    text: string;
    href: string;
  };
};

type DifferentiatorContent =
  | (DifferentiatorContentBaseProps & {
      image: FeaturedImageProps;
    })
  | (DifferentiatorContentBaseProps & {
      /** Pass in a image element defined locally in place of FeaturedImageProps */
      imageElement: JSX.Element;
    });

export type DifferentiationProps = {
  content: DifferentiatorContent[];
  /** @deprecated component uses css instead of prop value */
  isMobile?: boolean;
  isPhotoOnRight?: boolean;
  title: HighlightTitleProps;
};

const Differentiation: React.FC<DifferentiationProps> = ({
  content,
  isPhotoOnRight = true,
  title,
}): JSX.Element => {
  const firstItemId = content[0].id;
  const [selectedSection, setSelectedSection] = useState<string>(firstItemId);
  const differenceTitle = (
    <TitleWrapper>
      <HighlightTitle {...title} isH1={false} />
    </TitleWrapper>
  );

  const renderImage = (item: DifferentiatorContent, key?: string) => {
    if ('imageElement' in item) {
      return item.imageElement;
    }
    return <FeaturedImage {...item.image} key={key} />;
  };
  const accordionItems = content.map((item, index) => ({
    id: index,
    hed: item.subtitle,
    dek: (
      <DekWrapper>
        {item.description}
        <AccordionImageWrapper>{renderImage(item)}</AccordionImageWrapper>

        {item.button && (
          <ButtonWrapper>
            <ButtonV3 component="a" href={item.button.href} textTransform size="small">
              {item.button.text}
            </ButtonV3>
          </ButtonWrapper>
        )}
      </DekWrapper>
    ),
  }));

  const Menu = content.map((item, index) => {
    const isCurrentSelection = selectedSection === item.id;

    return (
      <MenuItem key={`difference-key-${index}`} isSelected={isCurrentSelection}>
        {isCurrentSelection ? (
          <>
            <MenuItemTitle>
              {item.subtitle}
              <ArrowIcon
                aria-hidden="true"
                flip="horizontal"
                showTitle={false}
                width={1.5}
                height={-1}
                unit="em"
              />
            </MenuItemTitle>
            <MenuItemDescription>{item.description}</MenuItemDescription>
            {item.button && (
              <ButtonWrapper>
                <ButtonV3 component="a" href={item.button.href} textTransform>
                  {item.button.text}
                </ButtonV3>
              </ButtonWrapper>
            )}
          </>
        ) : (
          <UnselectedItemWrapper
            noUnderline
            onClick={() => setSelectedSection(item.id)}
            role="button"
            subtle
            inline
            arrow
          >
            {item.subtitle}
            <HiddenContent>
              {item.description}
              {item.button && (
                <ButtonV3 component="a" href={item.button.href} textTransform>
                  {item.button.text}
                </ButtonV3>
              )}
            </HiddenContent>
          </UnselectedItemWrapper>
        )}
      </MenuItem>
    );
  });

  const Images = content.map(
    (item, index) => selectedSection === item.id && renderImage(item, `difference-image-${index}`)
  );

  return (
    <DifferentiationWrapper>
      <HiddenOnDesktop>
        <AccordionWrapper>
          {differenceTitle}
          <StyledAccordion collapsed initialExpandedItems={0} items={accordionItems} />
        </AccordionWrapper>
      </HiddenOnDesktop>
      <OnlyOnDesktop>
        <SectionWrapper>
          <Sidebar isPhotoOnRight={isPhotoOnRight}>
            {differenceTitle}
            <MenuWrapper>{Menu}</MenuWrapper>
          </Sidebar>
          <ImageWrapper isPhotoOnRight={isPhotoOnRight}>{Images}</ImageWrapper>
        </SectionWrapper>
      </OnlyOnDesktop>
    </DifferentiationWrapper>
  );
};

export default Differentiation;
